import { FC } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { LocalStorageKeys } from '../../constants/LocalStorage'
import { useAuth } from '../../context/AuthProvider'
import { Permission } from '../../types/User'
import { setLocalStorageItem } from '../../utils/localStorageUtils'
import { PLATFORM_ROOT } from '../../utils/platformMode'

interface RequireAuthProps {
  allowedPermissions?: Permission | Permission[]
  /**
   * 'client' routes need a client user; 'platform' routes need a platform administrator;
   * 'any' only needs one of the two (e.g. the mode selector).
   */
  scope?: 'client' | 'platform' | 'any'
}

const RequireAuth: FC<RequireAuthProps> = ({ allowedPermissions, scope = 'client' }) => {
  const { auth, userHasPermission, isAuthenticated } = useAuth()
  const location = useLocation()

  if (!isAuthenticated()) {
    setLocalStorageItem(LocalStorageKeys.REQUESTED_PATH, location.pathname)
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // A platform-only administrator has no client to work on: send it to its own area.
  if (scope === 'client' && !auth.user) {
    return <Navigate to={PLATFORM_ROOT} replace />
  }

  if (scope === 'platform' && !auth.platformAdmin) {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />
  }

  if (
    allowedPermissions &&
    (!Array.isArray(allowedPermissions) || allowedPermissions.length > 0) &&
    !userHasPermission(allowedPermissions)
  ) {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />
  }

  return <Outlet />
}

export default RequireAuth
